import React, { useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { X, FileText, ListChecks, Share2, ChevronDown } from "lucide-react";

/* "How does welovehumans work?" - the explanation/FAQ popup behind the info icon
   in the header. Same overlay style as the support popup (backdrop blur, centered,
   top z-index); content in the company brand (Tailwind + palette + lucide). */

const STEPS = [
  { icon: FileText, titleId: "teamroom.howItWorks.step1.title", bodyId: "teamroom.howItWorks.step1.body" },
  { icon: ListChecks, titleId: "teamroom.howItWorks.step2.title", bodyId: "teamroom.howItWorks.step2.body" },
  { icon: Share2, titleId: "teamroom.howItWorks.step3.title", bodyId: "teamroom.howItWorks.step3.body" },
];

const FAQ = [
  { questionId: "teamroom.howItWorks.faq1.q", answerId: "teamroom.howItWorks.faq1.a" },
  { questionId: "teamroom.howItWorks.faq2.q", answerId: "teamroom.howItWorks.faq2.a" },
  { questionId: "teamroom.howItWorks.faq3.q", answerId: "teamroom.howItWorks.faq3.a" },
  { questionId: "teamroom.howItWorks.faq4.q", answerId: "teamroom.howItWorks.faq4.a" },
  { questionId: "teamroom.howItWorks.faq5.q", answerId: "teamroom.howItWorks.faq5.a" },
];

const HowItWorks = ({ onClose, onSupportClick }) => {
  const [open, setOpen] = useState(0);
  const intl = useIntl();

  useEffect(() => {
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 flex items-center justify-center bg-modal-background-blur bg-opacity-90 backdrop-blur-[13px] z-[999999] p-6"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative bg-white rounded-[36px] shadow-2xl w-full max-w-[640px] max-h-[86vh] overflow-y-auto custom-scrollbar p-9"
      >
        <button
          onClick={onClose}
          aria-label={intl.formatMessage({ id: "teamroom.common.close" })}
          className="absolute top-7 right-7 w-9 h-9 rounded-full inline-flex items-center justify-center text-gray-400 hover:bg-gray-100 hover:text-navy-blue transition"
        >
          <X size={20} strokeWidth={2.4} />
        </button>

        <h2 className="text-[26px] font-bold text-navy-blue leading-tight pr-8">
          <FormattedMessage id="teamroom.howItWorks.title" />
        </h2>
        <p className="text-gray-500 mt-2 mb-7">
          <FormattedMessage id="teamroom.howItWorks.intro" />
        </p>

        <div className="space-y-3">
          {STEPS.map((s, i) => (
            <div key={i} className="flex items-start gap-4 p-4 rounded-2xl bg-[#eef3f8]">
              <span className="shrink-0 w-11 h-11 rounded-2xl bg-standart-ocean/10 text-standart-ocean inline-flex items-center justify-center relative">
                <s.icon size={20} strokeWidth={2.2} />
                <span className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full bg-navy-blue text-white text-[11px] font-bold inline-flex items-center justify-center">{i + 1}</span>
              </span>
              <div className="min-w-0">
                <div className="font-bold text-navy-blue"><FormattedMessage id={s.titleId} /></div>
                <div className="text-[13.5px] text-gray-500 mt-0.5 leading-relaxed"><FormattedMessage id={s.bodyId} /></div>
              </div>
            </div>
          ))}
        </div>

        <h3 className="text-lg font-bold text-navy-blue mt-8 mb-1">
          <FormattedMessage id="teamroom.howItWorks.faqTitle" />
        </h3>
        <div className="divide-y divide-gray-100 border-y border-gray-100">
          {FAQ.map((f, i) => (
            <div key={i}>
              <button
                onClick={() => setOpen(open === i ? -1 : i)}
                className="w-full flex items-center justify-between gap-3 py-3.5 text-left"
              >
                <span className="font-bold text-[14.5px] text-navy-blue"><FormattedMessage id={f.questionId} /></span>
                <ChevronDown size={18} className={`shrink-0 text-standart-ocean transition-transform ${open === i ? "rotate-180" : ""}`} />
              </button>
              {open === i && <p className="text-[13.5px] text-gray-500 leading-relaxed pb-4 pr-6"><FormattedMessage id={f.answerId} /></p>}
            </div>
          ))}
        </div>

        <div className="mt-7 p-4 rounded-2xl bg-light-purple/10 text-center">
          <span className="text-[13.5px] text-navy-blue"><FormattedMessage id="teamroom.howItWorks.footer.text" /></span>
          <button type="button" onClick={onSupportClick} className="text-[13.5px] font-bold text-standart-ocean hover:underline">
            <FormattedMessage id="teamroom.howItWorks.footer.cta" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default HowItWorks;
